import React from "react";
import { VDBText } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import Popup from "@components/molecules/PopUp";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUpdateOrderRequest, WorkFlowActions } from "@apis/requestHistory";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { productNameToTokenCategory } from "@utils/index";
import { useQueryClient } from "@tanstack/react-query";
import { ProductCategory } from "@utils/constants";
import { messageType } from "@components/molecules/PopUp/types";

type ApproveOrderPopupType = {
  stockItemRequest: any;
  productType: ProductCategory;
  setApproveCodeShowPopup: any;
  onSuccess: () => void;
};

const ApproveOrderPopup = (props: ApproveOrderPopupType) => {
  const { stockItemRequest, productType, setApproveCodeShowPopup, onSuccess } =
    props;
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const activeProductThemeCategory = productNameToTokenCategory(productType);
  const updateStatus = useUpdateOrderRequest(stockItemRequest.id);
  const queryClient = useQueryClient();

  const popupStyles = {
    accept: {
      title: t(
        "requestHistoryDetail.approve.requestPopup.title.text",
        "Confirm Order Request"
      ),
      bodyTitle: t(
        "requestHistoryDetail.approve.requestPopup.description.text",
        "Do you really want to approve this order request?"
      ),
      showComments: false,
      primaryButtonText: t(
        "requestHistoryDetail.approve.requestPopup.primaryButton.text",
        "Yes"
      ),
      secondaryButtonText: t(
        "requestHistoryDetail.requestPopup.approveDismissButton.text",
        "Dismiss"
      ),
      primaryButtonOnPress: () => {
        updateRequestStatus();
      },
      secondaryButtonOnPress: () => {
        setApproveCodeShowPopup(false);
      },
    },
  };

  const updateRequestStatus = () => {
    updateStatus.mutate(
      {
        workflow_action: WorkFlowActions.APPROVE,
      },
      {
        onSuccess() {
          onSuccess?.();
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request_detail"] })
            .then();
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request"] })
            .then();
          setApproveCodeShowPopup(false);
          showToast(
            undefined,
            t("requestHistory.orderApproved.text", "Order Approved"),
            ToastTypes.SUCCESS,
            true,
            true
          );
        },
        onError(errorData: any) {
          setApproveCodeShowPopup(false);
          const errorMessage =
            errorData?.response?.data?.response?.header?.message;
          if (errorMessage) {
            showToast(undefined, errorMessage, ToastTypes.ERROR, true, true);
          }
        },
      }
    );
  };

  return (
    <Popup
      type={messageType.Generic}
      styleKey="Body-R"
      title={popupStyles?.accept?.title}
      buttons={[
        {
          type: ButtonType.primary,
          title: popupStyles?.accept?.primaryButtonText,
          onPressAction: popupStyles?.accept?.primaryButtonOnPress,
          loading: !!updateStatus.isLoading,
          disabled: !!updateStatus.isLoading,
        },
        {
          type: ButtonType.secondary,
          title: popupStyles?.accept?.secondaryButtonText,
          category: activeProductThemeCategory,
          onPressAction: popupStyles?.accept?.secondaryButtonOnPress,
        },
      ]}
      hasChildren={true}
    >
      {popupStyles?.accept?.bodyTitle && (
        <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
          {popupStyles?.accept?.bodyTitle}
        </VDBText>
      )}
    </Popup>
  );
};

export default ApproveOrderPopup;
